// Figma frame 22 · Contact support (33:1238) — US-132.
//
// The drawn form has a TOPIC select, SUBJECT, WHAT HAPPENED, a file
// dropzone and a "SAVE DRAFT" button. `POST /support` only ever stores
// `{subject, body}` (setup-backend Wave 1 handoff) — no attachment, no
// topic field, no draft persistence. Rather than invent backend fields for
// any of those: TOPIC is folded into the `subject` string that actually gets
// sent (never a new field, just prose composition of the one field the
// backend stores); the file dropzone and SAVE DRAFT are omitted outright,
// flagged in the progress notes. The "ATTACHED AUTOMATICALLY" panel's own
// copy ("we attach this so you don't have to") is made literally true by
// prepending the same values shown there to the ticket `body` — otherwise
// the panel would be describing something that doesn't happen.
//
// The one-business-day response promise is NOT shown unconditionally: per
// `docs/monitoring/README.md`'s support-page guidance, "Free tier gets no
// response promise. Say that plainly rather than leaving it implied" — so
// the description is conditioned on the org's real plan (`GET /licenses`),
// not copied verbatim from the mockup's "Pro includes…" string.

import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Select,
  Textarea,
} from "../../ui";
import { hrefs } from "../../shell/hrefs";
import { useLicense, useSubmitSupport } from "./api";
import "./ops.css";

const TOPICS = [
  { value: "docs-page", label: "Something looks wrong on my docs page" },
  { value: "billing", label: "Billing question" },
  { value: "feature", label: "Feature request" },
  { value: "other", label: "Other" },
];

// The backend's real cap (`assert_within_budget`, `backend/extract/budget.py`
// — the same budget `/try` uses): ~100k tokens, ~400k characters combined.
const BODY_CHAR_CAP = 400_000;

function planLabel(plan: string | null): string {
  if (!plan) return "No plan on file";
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

export function SupportScreen({ orgSlug }: { orgSlug: string }) {
  const navigate = useNavigate();
  const license = useLicense();
  const submit = useSubmitSupport();

  const [topic, setTopic] = useState(TOPICS[0].value);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  const topicLabel = TOPICS.find((t) => t.value === topic)?.label ?? "";
  const combinedLength = subject.length + message.length;
  const overCap = combinedLength > BODY_CHAR_CAP;

  const responseNote =
    license.data?.plan && license.data.plan !== "trial"
      ? `Your ${planLabel(license.data.plan)} plan includes email support with a one business day response.`
      : "We read every ticket, though there's no guaranteed response time on your current plan.";

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!subject.trim() || !message.trim() || overCap) return;

    const context = [
      `Workspace: ${orgSlug}`,
      license.data ? `Plan: ${planLabel(license.data.plan)}` : null,
      license.data?.domains.length ? `Bound domain: ${license.data.domains.join(", ")}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    submit.mutate(
      {
        subject: `[${topicLabel}] ${subject.trim()}`,
        body: `${context}\n\n${message.trim()}`,
      },
      {
        onSuccess: (result) => {
          setError(null);
          void navigate({
            to: hrefs(orgSlug).supportSent,
            search: { ticket: result.ticket_id },
          });
        },
        onError: (err) => {
          // The typed subject/message are untouched — losing a support
          // message on a network blip is the worst failure this screen can
          // have (US-132 acceptance criterion).
          setError(err.message || "Couldn't send that just now. Your message is still here — try again.");
        },
      },
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Support"
        title="Contact support"
        description={responseNote}
      />

      <div className="maisight-ops-support-columns" data-chassis="paper">
        <div className="maisight-ops-support-col-left">
          <Card header="New ticket">
            <form className="maisight-ops-form" onSubmit={handleSubmit}>
              <Field label="Topic" id="support-topic" help="Routing only. We read every ticket either way.">
                <Select
                  options={TOPICS}
                  value={topic}
                  onChange={(event) => setTopic(event.target.value)}
                />
              </Field>
              <Field label="Subject" id="support-subject" help="One line. What changed, and when.">
                <Input
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="Terms stopped highlighting after deploy"
                  required
                />
              </Field>
              <Field
                label="What happened"
                id="support-body"
                help={`Include a section and a term we can open. Up to ${BODY_CHAR_CAP.toLocaleString()} characters combined with the subject.`}
                error={overCap ? "That's over the length limit — trim it down." : undefined}
              >
                <Textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Highlighting worked through Tuesday. After we shipped a docs build on Wednesday the marks stopped appearing…"
                  rows={5}
                  required
                />
              </Field>
              {error ? (
                <p className="type-bodyS maisight-ops-privacy" role="alert">
                  {error}
                </p>
              ) : null}
              <div className="maisight-ops-actions">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submit.isPending || !subject.trim() || !message.trim() || overCap}
                >
                  {submit.isPending ? "Sending…" : "Send ticket"}
                </Button>
              </div>
            </form>
          </Card>
        </div>

        <div className="maisight-ops-support-col-right">
          <Card header="Attached automatically">
            <div className="maisight-ops-kv">
              <span className="type-labelSm maisight-ops-kv-label">Workspace</span>
              <span className="type-bodyS maisight-ops-kv-value">{orgSlug}</span>
            </div>
            <div className="maisight-ops-kv">
              <span className="type-labelSm maisight-ops-kv-label">Plan</span>
              <span className="type-bodyS maisight-ops-kv-value">
                {planLabel(license.data?.plan ?? null)}
              </span>
            </div>
            <div className="maisight-ops-kv">
              <span className="type-labelSm maisight-ops-kv-label">Bound domain</span>
              <span className="type-bodyS maisight-ops-kv-value">
                {license.data?.domains.length ? license.data.domains.join(", ") : "None yet"}
              </span>
            </div>
            <p className="type-bodyS maisight-ops-privacy">
              We attach this so you don't have to. No reader data is included
              — we don't collect any.
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}
